import { AfterContentInit, AfterViewChecked, AfterViewInit, Component, ComponentFactoryResolver, EventEmitter, Input, OnInit, Output, QueryList, SimpleChanges, ViewChild, ViewChildren } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { AdvancedTableService } from './advanced-table.service';
import { NgbSortableHeaderDirective, SortEvent } from './sortable.directive';
import { Router } from '@angular/router';
import { fillAndDownloadPDF } from 'src/app/core/helpers/pdfexport';
import { Arbitro } from 'src/app/arbitros/arbitros.model';
import { ArbitrosService } from 'src/app/arbitros/arbitros.service';
import { formatDateStringWithTime } from 'src/app/core/helpers/utils';
import { getCurrentLanguage } from 'src/app/core/helpers/utils';


export interface Column {
  name: string;
  label: string;
  formatter: (a: any) => any | string;
  sort?: boolean;
  width?: number;
}

@Component({
  selector: 'app-advanced-table',
  templateUrl: './advanced-table.component.html',
  styleUrls: ['./advanced-table.component.scss'],
  providers: [AdvancedTableService]
})
export class AdvancedTableComponent implements OnInit, AfterViewChecked {

  currentLanguage: string = getCurrentLanguage()
  // Expone la función para poder llamarla en el template
  public formatDateStringWithTime = formatDateStringWithTime;

  @Output() buttonClick = new EventEmitter<void>();
  @Output() deleteClick = new EventEmitter<any>();
  @Output() estadoClick = new EventEmitter<any>();
  @Output() editParteClick = new EventEmitter<any>();
  @Output() representantesClick = new EventEmitter<any>();

  // Buscaor de ficheros
  @Output() actionEmitter = new EventEmitter<{ action: string; folderId: string }>(); // Emitir eventos

  @Input() pagination: boolean = false;
  @Input() isSearchable: boolean = false;
  @Input() isSortable: boolean = false;
  @Input() pageSizeOptions: number[] = [];
  @Input() defaultPageSize: number = 10; // Tamaño por defecto inicial
  @Input() tableData: any[] = [];
  @Input() tableClasses: string = '';
  @Input() theadClasses: string = '';
  @Input() hasRowSelection: boolean = false;
  @Input() columns: Column[] = [];
  @Input() isPartesSection: boolean = false;
  @Input() isCasesSection: boolean = false;
  collectionSize: number = this.tableData.length;
  selectAll: boolean = false;
  isSelected: boolean[] = [];

  // Parámetros extra
  @Input() nacionalidades: any[] = [];
  @Input() profesiones: any[] = [];
  @Input() colegiaciones: any[] = [];
  @Input() sectores: any[] = [];
  @Input() idiomas: any[] = [];
  @Input() sistemasJuridicos: any[] = [];

  //Parámetros del botón
  @Input() buttonTitle: string = '';
  @Input() buttonTitle2: string = '';
  @Input() buttonLink: string = '';

  //Parámetro para desactivar todas las opciones
  @Input() disableOptions: boolean = false;
  @Input() disableNoAdmin: boolean = true;

  @Input() columnFilterValues: { [key: string]: string } | null = null;

  @Output() search = new EventEmitter<string>();
  @Output() sort = new EventEmitter<SortEvent>();
  @Output() handleTableLoad = new EventEmitter<any>();
  @Output() columnFilterChange = new EventEmitter<{ column: string; value: string }>();


  @ViewChildren(NgbSortableHeaderDirective) headers!: QueryList<NgbSortableHeaderDirective>;
  @ViewChildren('advancedTable') advancedTable!: any;

  constructor(public service: AdvancedTableService,
    private sanitizer: DomSanitizer,
    private componentFactoryResolver: ComponentFactoryResolver,
    private router: Router,
    private arbitroService: ArbitrosService) {
  }

  ngAfterViewChecked(): void {
    this.handleTableLoad.emit();

  }

  ngOnInit(): void {
    //console.log("disableNoAdmin",this.disableNoAdmin)
    for (let i = 0; i < this.tableData.length; i++) {
      this.isSelected[i] = false;
    }
    //console.log("tableData",this.tableData)
    this.service.pageSize = this.defaultPageSize; // Asigna el valor inicial del tamaño de página
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.paginate();
  }

  /**
   * sets pagination configurations
   */
  paginate(): void {
    // paginate
    this.service.totalRecords = this.tableData.length;
    if (this.service.totalRecords === 0) {
      this.service.startIndex = 0;
    }
    else {
      this.service.startIndex = ((this.service.page - 1) * this.service.pageSize) + 1;
    }
    this.service.endIndex = Number((this.service.page - 1) * this.service.pageSize + this.service.pageSize);
    if (this.service.endIndex > this.service.totalRecords) {
      this.service.endIndex = this.service.totalRecords;
    }
  }


  /**
   * Search Method
  */
  searchData(): void {
    this.search.emit(this.service.searchTerm);
  }

  onColumnFilterInput(column: string, value: string): void {
    this.columnFilterChange.emit({ column, value });
  }


  /**
   * sorts column
   * @param param0 column name,sort direction
   */
  onSort({ column, direction }: SortEvent): void {
    // resetting other headers
    this.headers.forEach(header => {
      if (header.sortable !== column) {
        header.direction = '';
      }
    });

    this.service.sortColumn = column;
    this.service.sortDirection = direction;

    this.sort.emit({ column, direction });
  }

  /**
   *  calls formatter function for table cells
   * @param column column name
   * @param data data of column
   */
  callFormatter(column: Column, data: any): any {
    return (column.formatter(data));
  }

  /**
   * @returns intermediate status of selectAll checkbox
   */
  checkIntermediate(): boolean {
    let selectedRowCount = this.isSelected.filter(x => x === true).length;
    if (!this.selectAll && selectedRowCount > 0 && selectedRowCount < this.tableData.length) {
      return true;
    }
    else {
      return false;
    }
  }

  /**
   * select all row
   * @param event event
   */
  selectAllRow(event: any): void {
    this.selectAll = !this.selectAll;
    if (this.selectAll) {
      for (let i = 0; i < this.tableData.length; i++) {
        this.isSelected[i] = true;
      }
    }
    else {
      for (let i = 0; i < this.tableData.length; i++) {
        this.isSelected[i] = false;
      }
    }
  }

  /**
   * selects row
   * @param index row index
   */
  selectRow(index: number): void {
    this.isSelected[index] = !this.isSelected[index];
    this.selectAll = (this.isSelected.filter(x => x === true).length === this.tableData.length);
  }

  /**
 * Alternativa para sustituir routerLink en el button
 * @param path Id del usuario
 */
  navigateTo(path: string): void {
    this.router.navigate([path]);
  }

  /**
* Alternativa para sustituir routerLink en los innerHTML EDIT
* @param id Id del usuario
*/
  navigateToEdit(path: string, id: string, newWindow: boolean): void {
    // Obtén el base path actual, que puede incluir el segmento de idioma.
    const fullPath = window.location.pathname;  // Esto debería darte la ruta actual incluyendo '/en/' si está presente.

    // Construye la URL completa.
    // Verifica si el segmento de idioma está presente y construye la URL en consecuencia.
    const basePath = fullPath.split('/')[1];  // Obtiene el primer segmento de la ruta, que podría ser 'en'.
    const newPath = basePath.match(/^(en|es)$/) ? `${basePath}/${path}/edit/${id}` : `${path}/edit/${id}`;
    const url = `${location.origin}/${newPath}`;

    // Abre la nueva pestaña.
    if (newWindow)
      window.open(url, '_blank');
    else
      window.open(url, '_self');

    //this.router.navigate([path+'/edit', id]);
    // Construye la URL completa. Suponiendo que tu aplicación está en la raíz del dominio.


    //const url = `${location.origin}/${path}/edit/${id}`;
    // Abre la nueva pestaña
    //window.open(url, '_blank');
  }


  /**
 * Alternativa para sustituir routerLink en los innerHTML DELETE
 * @param id Id del usuario
 */
  navigateToDelete(path: string, id: string): void {
    // Aquí se maneja las lógicas de borrado de todos los componentes
    if (path === 'partes') {
      const parte = this.tableData.find(record => record.id === id);
      if (parte) {
        this.deleteClick.emit(parte); // Emite el evento de borrado
      }
    }

    if (path === 'representantes') {
      const representante = this.tableData.find(record => record.id === id);
      if (representante) {
        this.deleteClick.emit(representante); // Emite el evento de borrado
      }
    }

    if (path === 'pagos') {
      const pago = this.tableData.find(record => record.id === id);
      if (pago) {
        this.deleteClick.emit(pago); // Emite el evento de borrado
      }
    }

    if (path === 'hitos') {
      const hito = this.tableData.find(record => record.id === id);
      if (hito) {
        this.deleteClick.emit(hito); // Emite el evento de borrado
      }
    }

    if (path === 'plantillas') {
      const plantilla = this.tableData.find(record => record.id === id);
      if (plantilla) {
        this.deleteClick.emit(plantilla); // Emite el evento de borrado
      }
    }

    if (path === 'usuarios') {
      const usuario = this.tableData.find(record => record.id === id);
      if (usuario) {
        this.deleteClick.emit(usuario); // Emite el evento de borrado
      }
    }

    if (path === 'arbitros') {
      const arbitro = this.tableData.find(record => record.id === id);
      if (arbitro) {
        this.deleteClick.emit(arbitro); // Emite el evento de borrado
      }
    }
  }

  /**
   * Alternativa para sustituir routerLink en los innerHTML DELETE
   * @param id Id del usuario
   */
  navigateToEditParte(path: string, id: string): void {
    // Aquí se maneja las lógicas de borrado de todos los componentes
    if (path === 'partes') {
      const parte = this.tableData.find(record => record.id === id);
      if (parte) {
        this.editParteClick.emit(parte); // Emite el evento de borrado
      }
    }
  }

  /**
 * Alternativa para sustituir routerLink en los innerHTML RE
 * @param id Id del usuario
 */
  navigateToRepresentantes(path: string, id: string): void {
    if (path === 'partes') {
      const parte = this.tableData.find(record => record.id === id);
      if (parte) {
        this.representantesClick.emit(parte);
      }
    }
  }


  /**
* Alternativa para sustituir routerLink en el button
* @param path Id del usuario
*/
  navigateToFolders(path: string, id: string): void {
    this.router.navigate([path + '/folders', id]);
  }

  /**
* Alternativa para sustituir routerLink en el button
* @param path Id del usuario
*/
  navigateToManage(path: string, id: string): void {
    this.router.navigate([path + '/gestion', id]);
  }

  /**
* Descargar CV Arbitro
* @param record arbitro
*/
  genericFillAndDownloadPDF(arbitro: Arbitro): void {
    if (arbitro.id) {
      this.arbitroService.getArbitro(arbitro.id).subscribe({
        next: (arbitroData) => {
          // Aquí manejas la respuesta cuando se obtiene el árbitro correctamente
          fillAndDownloadPDF(arbitroData, this.arbitroService);
        },
        error: (err) => {
          // Aquí manejas el error que pueda ocurrir
          console.error('Error al obtener el árbitro:', err);
        }
      });
    }
  }


  isEstadoCerrado(estado: string): boolean { 
    const estadosCerrados = ['CERRADO_LAUDO', 'CERRADO_ACUERDO', 'CERRADO_OTROS', 'CERRADO_REENVIO'];
    return estadosCerrados.includes(estado);
  }

  handleAction(action: string, folderId: string): void {
    this.actionEmitter.emit({ action, folderId }); // Emitir acción y folderId
  }

  exportCases() {
    this.service.getCasosXSL("EXCEL_EXPEDIENTES_COMPLETOS").subscribe({
      next: (response) => {
        // Extraer el nombre del archivo de Content-Disposition
        const contentDisposition = response.headers.get('Content-Disposition');
        let fileName = 'Casos.xlsx'; // Nombre por defecto
        //console.log("contentDisposition",contentDisposition)
  
        if (contentDisposition) {
          // Ajustar el regex para manejar nombres con espacios o caracteres especiales
          const match = contentDisposition.match(/filename\*?=(?:UTF-8'')?["]?([^"]+)["]?/);
          if (match && match[1]) {
            fileName = decodeURIComponent(match[1]);
          }
        }
  
        // Crear el blob y descargar el archivo
        const blob = new Blob([response.body!], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName; // Usar el nombre obtenido
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url); // Liberar la URL
      },
      error: (err) => {
        console.error('Error al obtener la descarga de Casos:', err);
      }
    });
  }

  truncateText(text: string, length: number): string {
    return text.length > length ? text.slice(0, length) + '...' : text;
  }
  

}
